import React, { useState, useCallback, useEffect, useRef } from 'react';
import { useChatStore } from '../../store/chatStore';
import { FocusedViewHeader } from './FocusedViewHeader';
import { ResizableFocusedPanel } from './ResizableFocusedPanel';
import type { PanelSize } from '../ToolExecution/types';
import { useSidebarStore } from '../../../../store/sidebarStore';
import { useLayoutStore } from '../../../../store/layoutStore';
import { PrintWrapper } from '../MessageContent/markdown/PrintWrapper';

import { useTranslation } from 'react-i18next';

export const FocusedViewPanel: React.FC = () => {
  const { t } = useTranslation('pleri/chat');
  const [panelSize, setPanelSize] = useState<PanelSize>('expanded');
  const contentRef = useRef<HTMLDivElement>(null);
  const { openSidebar } = useSidebarStore();
  const { isMiniMode, setMiniMode } = useLayoutStore();

  const isOpen = useChatStore((state) => {
    const activeConversation = state.activeConversationId
      ? state.conversations[state.activeConversationId]
      : null;
    return activeConversation?.focusedViewOpen || false;
  });

  const setOpen = useChatStore((state) => state.setFocusedViewOpen);
  const content = useChatStore((state) => state.focusedViewContent);
  const title = useChatStore((state) => state.focusedViewTitle);
  const printMode = useChatStore(
    (state) => state.focusedViewPrintMode || 'text',
  );

  const activeConversationId = useChatStore(
    (state) => state.activeConversationId,
  );

  // Auto-switch to fullscreen when focused view opens in mini mode
  useEffect(() => {
    if (isOpen && isMiniMode) {
      setMiniMode(false);
    }
  }, [isOpen, isMiniMode, setMiniMode]);

  const handleClose = useCallback(() => {
    if (activeConversationId) {
      setOpen(activeConversationId, false);
    }
    setPanelSize('expanded');
    // Clear the content when closing
    useChatStore.getState().setFocusedViewContent(null);
    useChatStore.getState().setFocusedViewPrintMode('text');
    // Open sidebar when closing focus view
    openSidebar();
  }, [activeConversationId, setOpen, setPanelSize, openSidebar]);

  if (!isOpen || !content) {
    return null;
  }

  const displayTitle = title || t('focusedView');
  const pdfFilename = `${displayTitle.toLowerCase().replace(/\s+/g, '-')}.pdf`;

  return (
    <ResizableFocusedPanel panelSize={panelSize}>
      <FocusedViewHeader
        panelSize={panelSize}
        onPanelSizeChange={setPanelSize}
        onClose={handleClose}
        title={displayTitle}
        contentRef={contentRef}
        pdfFilename={pdfFilename}
        printMode={printMode}
      />
      <div
        ref={contentRef}
        className={`custom-scrollbar flex-1 overflow-y-auto ${
          panelSize === 'fullscreen' ? 'container mx-auto' : ''
        }`}
      >
        {printMode === 'text' ? (
          <PrintWrapper>{content}</PrintWrapper>
        ) : (
          content
        )}
      </div>
    </ResizableFocusedPanel>
  );
};
